import {
  Box,
  styled,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  TextField,
  Button,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useEnv } from 'src/context/EnvProvider';
import useLoadDataList from 'src/hook/useLoadDataList';
import { getAllAssemblies } from 'src/services/assemblies';
import { getGranits } from 'src/services/granits';
import { addStock, GetPiece, getPieces, PieceInAssembly } from 'src/services/stock';
import { handleMutation } from 'src/utils/api.utils';

export const AddPieceStock = () => {
  const { data: granits } = useLoadDataList(getGranits);
  const { buckets } = useEnv();

  const [selectedPiece, setSelectedPiece] = useState<GetPiece>();
  const [selectedGranitId, setSelectedGranitId] = useState<string>();

  const { data: pieces, onRefresh, refreshing: isLoadingStockPieces } = useLoadDataList(getPieces);

  const [dimensions, setDimensions] = useState<{
    x: number;
    y: number;
    z: number;
  }>({ x: 0, y: 0, z: 0 });

  const [quantity, setQuantity] = useState('');
  const [price, setPrice] = useState('');
  const [name, setName] = useState('');

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (!selectedPiece || !selectedGranitId) return;

    handleMutation(
      (data) => addStock(data),
      {
        name,
        quantity: Number.parseInt(quantity, 10),
        price: Number.parseFloat(price),
        stockPieces: [
          {
            pieceId: selectedPiece.id,
            granitId: selectedGranitId,
            newGeometry: [],
            x: dimensions.x,
            y: dimensions.y,
            z: dimensions.z,
          },
        ],
      },
      () => {
        toast.success('Stock ajouté avec succès');
        onRefresh();
      },
    );
  };

  return (
    <Container>
      <Form onSubmit={handleSubmit}>
        <FormControl fullWidth sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Row>
            <FormControl sx={{ width: '100%' }}>
              <InputLabel id="piece-label">Choisir une pièce</InputLabel>
              <Select
                labelId="piece-label"
                id="piece-select"
                value={selectedPiece}
                label="Pièce"
                onChange={(e) => {
                  const piece = pieces?.find((p) => p.id === Number(e.target.value));
                  setSelectedPiece(piece);
                  setName(piece?.name || '');
                }}
                required
                renderValue={(value) => {
                  const piece = pieces?.find((p) => p.id === Number(value));
                  return piece?.name;
                }}
              >
                {pieces?.map((piece) => {
                  const image = piece.pictureUrl;

                  return (
                    <MenuItem
                      key={piece.id}
                      value={piece.id.toString()}
                      sx={{ display: 'flex', alignItems: 'center', gap: 2, flexDirection: 'row' }}
                    >
                      {image && (
                        <Box
                          component="img"
                          src={`${buckets?.BUCKET_3D_PIECES || ''}/${image}`}
                          alt={piece.name}
                          sx={{ width: 50, height: 50, objectFit: 'cover' }}
                        />
                      )}
                      <Typography variant="body1">{piece.name}</Typography>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <FormControl sx={{ width: '300px' }}>
              <InputLabel id="granit-label">Choisir un granit</InputLabel>
              <Select
                labelId="granit-label"
                value={selectedGranitId}
                onChange={(e) => setSelectedGranitId(e.target.value)}
                label="Granit"
                required
                renderValue={(value) => {
                  const granit = granits?.find((g) => g.id === value);
                  return granit?.name;
                }}
              >
                {granits
                  ?.filter((g) => {
                    if (selectedPiece?.pieceFabrication) {
                      const fabricationPiece = selectedPiece?.pieceFabrication.find(
                        (p) => p.fabrication === g.fabrication,
                      );
                      return !!fabricationPiece;
                    }
                    return g.fabrication === 'FRENCH';
                  })
                  .map((granit) => (
                    <MenuItem
                      key={granit.id}
                      value={granit.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        flexDirection: 'row',
                      }}
                    >
                      {granit.image && (
                        <Box
                          component="img"
                          src={`${buckets?.BUCKET_3D_PIECES || ''}/${granit.image}`}
                          alt={granit.name}
                          sx={{ width: 50, height: 50, objectFit: 'cover' }}
                        />
                      )}
                      <Typography variant="body1">{granit.name}</Typography>
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Row>

          <Row>
            <TextField
              label="Longueur"
              type="string"
              value={dimensions.z === 0 ? '' : dimensions.z}
              onChange={(e) => setDimensions({ ...dimensions, z: Number(e.target.value) })}
            />
            <TextField
              label="Largeur"
              type="string"
              value={dimensions.x === 0 ? '' : dimensions.x}
              onChange={(e) => setDimensions({ ...dimensions, x: Number(e.target.value) })}
            />
            <TextField
              label="Hauteur"
              type="string"
              value={dimensions.y === 0 ? '' : dimensions.y}
              onChange={(e) => setDimensions({ ...dimensions, y: Number(e.target.value) })}
            />
          </Row>
        </FormControl>

        <TextField
          fullWidth
          label="Quantité"
          type="number"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          required
          inputProps={{ min: 1 }}
        />

        <TextField
          fullWidth
          label="Prix"
          type="number"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          required
          inputProps={{ min: 0, step: 0.01 }}
        />

        <TextField
          fullWidth
          label="Nom"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />

        <Button type="submit" variant="contained" color="primary" fullWidth>
          Ajouter au stock
        </Button>
      </Form>
    </Container>
  );
};

const Container = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
});

const Form = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
});

const Row = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
});
